import React, { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { CheckCircle2, Loader2, ShoppingBag, XCircle, Star } from 'lucide-react';
import { paymentStatus, fetchOrder, fetchAppointment } from '../api';
import { useCart } from '../context/CartContext';

const MAX_POLLS = 8;

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState('checking'); // checking | paid | pending | error
  const [order, setOrder] = useState(null);
  const [appointment, setAppointment] = useState(null);
  const { clear } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    let polls = 0;
    let timer;
    const poll = async () => {
      if (!sessionId) { setState('error'); return; }
      try {
        const res = await paymentStatus(sessionId);
        if (res.payment_status === 'paid') {
          if (res.order_id) { clear(); try { setOrder(await fetchOrder(res.order_id)); } catch (e) {} }
          if (res.appointment_id) { try { setAppointment(await fetchAppointment(res.appointment_id)); } catch (e) {} }
          setState('paid');
          return;
        }
        if (['failed', 'expired'].includes(res.payment_status)) { setState('error'); return; }
      } catch (e) { setState('error'); return; }
      polls += 1;
      if (polls >= MAX_POLLS) { setState('pending'); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="pt-40 pb-40 min-h-screen">
      <div className="max-w-lg mx-auto px-5 text-center card-glass rounded-3xl p-12">
        {state === 'checking' && (<>
          <Loader2 className="h-14 w-14 text-[#f0d582] mx-auto mb-6 animate-spin" />
          <h1 data-testid="payment-checking" className="font-cinzel text-3xl text-gold-gradient">Confirming your payment…</h1>
          <p className="font-cormorant text-xl text-[#f5ecd9]/70 mt-3">One moment while we align the energy.</p>
        </>)}
        {state === 'paid' && (<>
          <div className="h-16 w-16 mx-auto rounded-full bg-gradient-to-br from-[#d6197d] to-[#ff4da6] flex items-center justify-center mb-6"><CheckCircle2 className="h-8 w-8 text-white" /></div>
          <h1 data-testid="payment-paid" className="font-cinzel text-3xl text-gold-gradient">Thank You!</h1>
          {appointment ? (
            <>
              <p className="font-cormorant text-xl text-[#f5ecd9]/75 mt-3">Your booking for <span className="text-[#f0d582]">{appointment.service_name}</span> is confirmed and paid.</p>
              <div className="mt-6 text-left border-t border-white/10 pt-5 font-jost text-sm text-[#f5ecd9]/70">
                <div className="flex justify-between mb-1"><span>Date</span><span>{appointment.date} at {appointment.time}</span></div>
                <div className="flex justify-between mb-1"><span>{appointment.payment_mode === 'deposit' ? 'Deposit paid' : 'Paid'}</span><span className="text-[#f0d582]">${(appointment.paid_amount || 0).toFixed(2)}</span></div>
                {appointment.payment_mode === 'deposit' && <div className="flex justify-between"><span>Balance at session</span><span>${(appointment.price - (appointment.paid_amount || 0)).toFixed(2)}</span></div>}
              </div>
            </>
          ) : (
            <p className="font-cormorant text-xl text-[#f5ecd9]/75 mt-3">Your order is confirmed and paid. A radiant ritual is on its way.</p>
          )}
          {order && (<div data-testid="payment-order-summary" className="mt-6 text-left border-t border-white/10 pt-5">
            <p className="font-jost text-xs uppercase tracking-widest text-[#f5ecd9]/40 mb-3">{order.order_no || `Order #${order.id.slice(0, 8)}`}</p>
            {order.items.map((it) => (<div key={it.name} className="flex justify-between font-jost text-sm text-[#f5ecd9]/70 mb-1"><span>{it.name} × {it.qty}</span><span>${(it.price * it.qty).toFixed(2)}</span></div>))}
            {order.discount > 0 && (<>
              <div className="flex justify-between font-jost text-sm text-[#f5ecd9]/60 mt-3 pt-3 border-t border-white/10"><span>Subtotal</span><span>${order.amount.toFixed(2)}</span></div>
              <div className="flex justify-between font-jost text-sm text-[#ff4da6] mb-1"><span>Discount</span><span>−${order.discount.toFixed(2)}</span></div>
              <div className="flex justify-between font-cinzel text-gold-gradient mt-1"><span>Total paid</span><span>${(order.net ?? (order.amount - order.discount)).toFixed(2)}</span></div>
            </>)}
            {!(order.discount > 0) && (<div className="flex justify-between font-cinzel text-gold-gradient mt-3 pt-3 border-t border-white/10"><span>Total paid</span><span>${(order.net ?? order.amount).toFixed(2)}</span></div>)}
          </div>)}
          <Link to="/shop" className="btn-gold inline-flex items-center gap-2 mt-8 rounded-full px-8 py-3.5 text-sm font-semibold text-[#0a0708] bg-gradient-to-r from-[#d4af37] via-[#f0d582] to-[#d4af37]"><ShoppingBag className="h-4 w-4" /> Continue Shopping</Link>
          {order && order.items && order.items[0] && order.items[0].id && (
            <div className="mt-8 pt-6 border-t border-white/10" data-testid="review-request">
              <p className="font-cormorant text-lg text-[#f5ecd9]/75">Loved your order? Your words help others discover Prestige Elements.</p>
              <Link to={`/product/${order.items[0].id}`} className="inline-flex items-center gap-2 mt-3 font-jost text-sm text-[#f0d582] hover:text-[#ff4da6] underline" data-testid="leave-review-link"><Star className="h-4 w-4" /> Leave a Review</Link>
            </div>
          )}
        </>)}
        {state === 'pending' && (<>
          <Loader2 className="h-14 w-14 text-[#f0d582] mx-auto mb-6" />
          <h1 data-testid="payment-pending" className="font-cinzel text-3xl text-gold-gradient">Payment Processing</h1>
          <p className="font-cormorant text-xl text-[#f5ecd9]/70 mt-3">Your payment is still processing. We'll email your confirmation shortly.</p>
          <Link to="/" className="inline-block mt-8 text-[#f0d582] underline">Back Home</Link>
        </>)}
        {state === 'error' && (<>
          <div className="h-16 w-16 mx-auto rounded-full border border-[#ff4da6]/50 flex items-center justify-center mb-6"><XCircle className="h-8 w-8 text-[#ff4da6]" /></div>
          <h1 data-testid="payment-error" className="font-cinzel text-3xl text-gold-gradient">Something went wrong</h1>
          <p className="font-cormorant text-xl text-[#f5ecd9]/70 mt-3">We couldn't confirm this payment. If you were charged, please contact us.</p>
          <button onClick={() => navigate('/shop')} className="btn-gold inline-flex items-center gap-2 mt-8 rounded-full px-8 py-3.5 text-sm font-semibold text-[#0a0708] bg-gradient-to-r from-[#d4af37] via-[#f0d582] to-[#d4af37]">Return to Shop</button>
        </>)}
      </div>
    </div>
  );
};

export default PaymentSuccess;
